import type { ColumnDef } from "@tanstack/react-table"
import { useCallback, useMemo } from "react"
import { useShallow } from "zustand/shallow"

import { GoodsReceiptLotTestStatusIndicator } from "../components/GoodsReceiptPalletsTable/cells/GoodsReceiptLotTestStatusIndicator"
import type { PurchaseOrderLineWithReceiptContext } from "../types"
import { calculateGoodsReceiptPalletRowTotals, formatPalletCount } from "../utils"

import { ProductStatusCell, PurchaseOrderNumberCell } from "@/components/table"
import type { GoodsReceiptPalletRow } from "@/graphql/codegen/graphql"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import { getUomName } from "@/utils/uom"

export const useCheckedInPurchaseOrderColumns = (): ColumnDef<PurchaseOrderLineWithReceiptContext>[] => {
  const units = useUnitOfMeasurementsStore(useShallow((s) => s.units))

  const getLots = useCallback(
    (palletRows: GoodsReceiptPalletRow[]) => palletRows.flatMap((palletRow) => palletRow.lots ?? []),
    []
  )

  return useMemo<ColumnDef<PurchaseOrderLineWithReceiptContext>[]>(() => {
    return [
      {
        accessorKey: "orderId",
        header: "PO #",
        cell: ({ row }) => {
          const order = row.original.orderDetail?.order
          return order ? <PurchaseOrderNumberCell order={order} /> : null
        },
      },
      {
        accessorKey: "productId",
        header: "Product ID",
        cell: ({ row }) => <ProductStatusCell product={row.original.product!} />,
      },
      {
        accessorKey: "akrochemDescription",
        header: "Akro Description",
        cell: ({ row }) => row.original.product?.companyProductDescription,
      },
      {
        accessorKey: "lotNumber",
        header: "Lot #",
        cell: ({ row }) => {
          const lots = getLots(row.original.palletRows)

          return (
            <div className='flex flex-col gap-1'>
              {lots.map((lot) => (
                <div key={lot.goodsReceiptPalletRowLotId} className='flex items-center gap-1'>
                  <GoodsReceiptLotTestStatusIndicator status={lot?.productTest?.status} />
                  <div>{lot.lotNumber}</div>
                </div>
              ))}
            </div>
          )
        },
      },
      {
        accessorKey: "batchNumber",
        header: "Batch #",
        cell: ({ row }) => {
          const lots = getLots(row.original.palletRows)

          return (
            <div className='flex flex-col gap-1'>
              {lots.map((lot) => (
                <div key={lot.goodsReceiptPalletRowLotId}>{lot.batchNumber || "-"}</div>
              ))}
            </div>
          )
        },
      },
      {
        accessorKey: "quantity",
        header: "Qty",
        cell: ({ row }) => {
          const { palletsCount, quantity } = calculateGoodsReceiptPalletRowTotals(row.original.palletRows)

          return (
            <>
              {formatPalletCount(palletsCount)}
              <br />
              {quantity} {getUomName(units, row.original.product?.packageUsWeightUom)}
            </>
          )
        },
      },
    ]
  }, [units, getLots])
}
